import React, { useEffect, useState } from "react";
import { 
  Home, 
  Plus, 
  Search, 
  Filter, 
  Edit, 
  Trash2, 
  Eye,
  Upload,
  X,
  Save,
  Globe,
  Mail,
  Phone
} from "react-feather";
import { 
  collection, 
  getDocs, 
  doc, 
  addDoc,
  updateDoc, 
  deleteDoc,
  query,
  orderBy
} from "firebase/firestore";
import { db, uploadImage } from "shared/utils/firebase";
import PageHeader from "shared/components/PageHeader/PageHeader";
import styles from "./CompanyManagement.module.css";

function CompanyManagement() {
  const [companies, setCompanies] = useState([]);
  const [filteredCompanies, setFilteredCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [companyToDelete, setCompanyToDelete] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoProgress, setLogoProgress] = useState(0);

  const [companyForm, setCompanyForm] = useState({
    name: "",
    logo: "",
    logoUrl: "",
    website: "",
    email: "",
    phone: "",
    description: "",
    industry: "",
    founded: "",
    headquarters: "",
    size: "",
    isPartner: true
  });

  useEffect(() => {
    fetchCompanies();
  }, []);

  useEffect(() => {
    filterCompanies();
  }, [companies, searchTerm]);

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      const companiesSnapshot = await getDocs(
        query(collection(db, "companies"), orderBy("name", "asc"))
      );
      const companiesData = companiesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate()
      }));
      setCompanies(companiesData);
    } catch (error) {
      console.error("Error fetching companies:", error);
    } finally {
      setLoading(false);
    }
  };

  const filterCompanies = () => {
    let filtered = companies;

    if (searchTerm) {
      filtered = filtered.filter(company => 
        company.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        company.industry?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        company.headquarters?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredCompanies(filtered);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCompanyForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingLogo(true);
    uploadImage(
      file,
      (progress) => {
        setLogoProgress(progress);
      },
      (url) => {
        setCompanyForm(prev => ({ ...prev, logo: url, logoUrl: url }));
        setUploadingLogo(false);
        setLogoProgress(0);
      },
      (error) => {
        console.error("Logo upload error:", error);
        setUploadingLogo(false);
        setLogoProgress(0);
      }
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const companyData = {
        ...companyForm,
        logo: companyForm.logo || companyForm.logoUrl,
        createdAt: editingCompany ? editingCompany.createdAt : new Date(),
        updatedAt: new Date()
      };

      if (editingCompany) {
        await updateDoc(doc(db, "companies", editingCompany.id), companyData);
        setCompanies(companies.map(company => 
          company.id === editingCompany.id ? { ...company, ...companyData } : company
        ));
      } else {
        const docRef = await addDoc(collection(db, "companies"), companyData);
        setCompanies([{ id: docRef.id, ...companyData }, ...companies]);
      }

      setShowCompanyModal(false);
      setEditingCompany(null);
      resetForm();
    } catch (error) {
      console.error("Error saving company:", error);
    }
  };

  const handleEdit = (company) => {
    setEditingCompany(company);
    setCompanyForm({
      name: company.name || "",
      logo: company.logo || "",
      logoUrl: company.logoUrl || company.logo || "",
      website: company.website || "",
      email: company.email || "",
      phone: company.phone || "",
      description: company.description || "",
      industry: company.industry || "",
      founded: company.founded || "",
      headquarters: company.headquarters || "",
      size: company.size || "",
      isPartner: company.isPartner !== undefined ? company.isPartner : true
    });
    setShowCompanyModal(true);
  };

  const handleDelete = async () => {
    if (!companyToDelete) return;

    try {
      await deleteDoc(doc(db, "companies", companyToDelete.id));
      setCompanies(companies.filter(company => company.id !== companyToDelete.id));
      setShowDeleteModal(false);
      setCompanyToDelete(null);
    } catch (error) {
      console.error("Error deleting company:", error);
    }
  };

  const resetForm = () => {
    setCompanyForm({
      name: "",
      logo: "",
      logoUrl: "",
      website: "",
      email: "",
      phone: "",
      description: "",
      industry: "",
      founded: "",
      headquarters: "",
      size: "",
      isPartner: true
    });
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-2">Loading companies...</p>
      </div>
    );
  }

  return (
    <div className={styles.companyManagement}>
      <PageHeader
        icon={Home}
        title="Partner Company Management"
        subtitle="Manage semiconductor industry partners, hiring recruiters, and company profiles"
        badge={`${filteredCompanies.length} Companies`}
        actions={
          <button 
            className={styles.addBtn}
            onClick={() => {
              setEditingCompany(null);
              resetForm();
              setShowCompanyModal(true);
            }}
          >
            <Plus size={16} className="me-2" />
            Add Company
          </button>
        }
      />

      <div className={styles.filters}>
        <div className={styles.searchBox}>
          <Search size={18} className={styles.searchIcon} />
          <input
            type="text"
            placeholder="Search companies..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.searchInput}
          />
        </div>
      </div>

      <div className={styles.stats}>
        <div className={styles.statCard}>
          <div className={styles.statNumber}>{companies.length}</div>
          <div className={styles.statLabel}>Total Companies</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statNumber}>
            {companies.filter(c => c.isPartner).length}
          </div>
          <div className={styles.statLabel}>Partners</div>
        </div>
      </div>

      <div className={styles.companyGrid}>
        {filteredCompanies.map((company) => (
          <div key={company.id} className={styles.companyCard}>
            <div className={styles.companyHeader}>
              <div className={styles.companyLogoContainer}>
                <img 
                  src={company.logo || company.logoUrl || "./company_logos/default.png"} 
                  alt={company.name}
                  className={styles.companyLogo}
                  onError={(e) => {
                    e.target.src = "./company_logos/default.png";
                  }}
                />
              </div>
              <div className={styles.companyActions}>
                <button
                  className={styles.actionBtn}
                  onClick={() => handleEdit(company)}
                  title="Edit Company"
                >
                  <Edit size={16} />
                </button>
                <button
                  className={`${styles.actionBtn} ${styles.deleteBtn}`}
                  onClick={() => {
                    setCompanyToDelete(company);
                    setShowDeleteModal(true);
                  }}
                  title="Delete Company"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>

            <div className={styles.companyContent}>
              <h5 className={styles.companyName}>{company.name}</h5>
              {company.industry && (
                <p className={styles.companyIndustry}>{company.industry}</p>
              )}
              {company.description && (
                <p className={styles.companyDescription}>{company.description}</p>
              )}
              <div className={styles.companyDetails}>
                {company.headquarters && (
                  <div className={styles.detailItem}>
                    <Globe size={14} className="me-1" />
                    <span>{company.headquarters}</span>
                  </div>
                )}
                {company.website && (
                  <div className={styles.detailItem}>
                    <Globe size={14} className="me-1" />
                    <a href={company.website} target="_blank" rel="noopener noreferrer">
                      {company.website}
                    </a>
                  </div>
                )}
                {company.email && (
                  <div className={styles.detailItem}>
                    <Mail size={14} className="me-1" />
                    <span>{company.email}</span>
                  </div>
                )}
                {company.phone && (
                  <div className={styles.detailItem}>
                    <Phone size={14} className="me-1" />
                    <span>{company.phone}</span>
                  </div>
                )}
              </div>
              {company.isPartner && (
                <span className="badge bg-success mt-2">Partner</span>
              )}
            </div>
          </div>
        ))}
      </div>

      {filteredCompanies.length === 0 && (
        <div className={styles.emptyState}>
          <Home size={48} className="text-muted mb-3" />
          <h5>No companies found</h5>
          <p className="text-muted">
            {searchTerm 
              ? "Try adjusting your search"
              : "No companies have been added yet"
            }
          </p>
          <button 
            className="btn btn-primary"
            onClick={() => {
              setEditingCompany(null);
              resetForm();
              setShowCompanyModal(true);
            }}
          >
            <Plus size={16} className="me-2" />
            Add First Company
          </button>
        </div>
      )}

      {/* Company Form Modal */}
      {showCompanyModal && (
        <div className={styles.modalOverlay} onClick={() => setShowCompanyModal(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h5>{editingCompany ? 'Edit Company' : 'Add New Company'}</h5>
              <button 
                className={styles.closeBtn}
                onClick={() => setShowCompanyModal(false)}
              >
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className={styles.modalBody}>
              <div className={styles.formGrid}>
                <div className={styles.formGroup}>
                  <label>Company Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={companyForm.name}
                    onChange={handleInputChange}
                    required
                    className={styles.formInput}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label>Industry</label>
                  <input
                    type="text"
                    name="industry"
                    value={companyForm.industry}
                    onChange={handleInputChange}
                    className={styles.formInput}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label>Website</label>
                  <input
                    type="url"
                    name="website"
                    value={companyForm.website}
                    onChange={handleInputChange}
                    className={styles.formInput}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label>Email</label>
                  <input
                    type="email"
                    name="email"
                    value={companyForm.email}
                    onChange={handleInputChange}
                    className={styles.formInput}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label>Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    value={companyForm.phone}
                    onChange={handleInputChange}
                    className={styles.formInput}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label>Headquarters</label>
                  <input
                    type="text"
                    name="headquarters"
                    value={companyForm.headquarters}
                    onChange={handleInputChange}
                    className={styles.formInput}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label>Founded</label>
                  <input
                    type="text"
                    name="founded"
                    value={companyForm.founded}
                    onChange={handleInputChange}
                    placeholder="e.g., 1971"
                    className={styles.formInput}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label>Size</label>
                  <input
                    type="text"
                    name="size"
                    value={companyForm.size}
                    onChange={handleInputChange}
                    placeholder="e.g., 10,000+ employees"
                    className={styles.formInput}
                  />
                </div>
              </div>

              <div className={styles.formGroup}>
                <label>Description</label>
                <textarea
                  name="description"
                  value={companyForm.description}
                  onChange={handleInputChange}
                  rows="4"
                  className={styles.formInput}
                />
              </div>

              <div className={styles.formGroup}>
                <label>Company Logo</label>
                <div className={styles.imageUpload}>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className={styles.fileInput}
                    id="company-logo"
                  />
                  <label htmlFor="company-logo" className={styles.fileLabel}>
                    <Upload size={16} className="me-2" />
                    Choose Logo
                  </label>
                  {uploadingLogo && (
                    <div className={styles.uploadProgress}>
                      <div className="progress">
                        <div 
                          className="progress-bar" 
                          style={{ width: `${logoProgress}%` }}
                        />
                      </div>
                      <small>{logoProgress.toFixed(1)}% uploaded</small>
                    </div>
                  )}
                  {companyForm.logo && (
                    <div className={styles.imagePreview}>
                      <img src={companyForm.logo} alt="Company logo" />
                    </div>
                  )}
                </div>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    name="isPartner"
                    checked={companyForm.isPartner}
                    onChange={handleInputChange}
                    className={styles.checkbox}
                  />
                  <span>Partner Company</span>
                </label>
              </div>

              <div className={styles.modalActions}>
                <button 
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowCompanyModal(false)}
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  className="btn btn-primary"
                >
                  <Save size={16} className="me-2" />
                  {editingCompany ? 'Update Company' : 'Add Company'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && companyToDelete && (
        <div className={styles.modalOverlay} onClick={() => setShowDeleteModal(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h5>Confirm Deletion</h5>
              <button 
                className={styles.closeBtn}
                onClick={() => setShowDeleteModal(false)}
              >
                <X size={20} />
              </button>
            </div>
            <div className={styles.modalBody}>
              <p>
                Are you sure you want to delete <strong>"{companyToDelete.name}"</strong>?
              </p>
              <p className="text-danger">
                This action cannot be undone.
              </p>
              <div className={styles.modalActions}>
                <button 
                  className="btn btn-secondary"
                  onClick={() => setShowDeleteModal(false)}
                >
                  Cancel
                </button>
                <button 
                  className="btn btn-danger"
                  onClick={handleDelete}
                >
                  Delete Company
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CompanyManagement;

